import { DatesRangeValue } from '@mantine/dates';
import { createEvent, EventCallable, sample } from 'effector';
import { spread } from 'patronum';
import { getToday } from '../utils';
import { createState, State } from './create-state';

export type CreateDateFilterModelParams = {
	fromChange: EventCallable<string>;
	toChange: EventCallable<string>;
	reset: EventCallable<void>;
	optional?: boolean;
};

export type DateFilterModel = {
	dateFilterDropdownClosed: EventCallable<void>;
	dateFilter: State<DatesRangeValue<string>>;
	optional?: boolean;
};

export const createDateFilterModel = ({
	fromChange,
	toChange,
	reset,
	optional = false,
}: CreateDateFilterModelParams) => {
	const [todayDate] = getToday();

	const dateFilter = createState<DatesRangeValue<string>>(
		optional ? [null, null] : [todayDate, todayDate],
	);

	const dateFilterDropdownClosed = createEvent();

	sample({
		clock: dateFilter.$value,
		filter: ([from, to]) => from !== null && to !== null,
		fn: ([from, to]) => ({ from: from!, to: to! }),
		target: spread({
			from: fromChange,
			to: toChange,
		}),
	});

	if (optional) {
		sample({
			clock: dateFilter.$value,
			filter: ([from, to]) => from === null && to === null,
			fn: () => ({ from: '', to: '' }),
			target: spread({
				from: fromChange,
				to: toChange,
			}),
		});

		sample({
			clock: dateFilterDropdownClosed,
			source: dateFilter.$value,
			filter: ([from, to]) => (from === null) !== (to === null),
			target: dateFilter.$value.reinit,
		});
	} else {
		sample({
			clock: dateFilterDropdownClosed,
			source: dateFilter.$value,
			filter: ([from, to]) => from === null || to === null,
			target: dateFilter.$value.reinit,
		});
	}

	sample({
		clock: reset,
		target: dateFilter.$value.reinit,
	});

	return {
		dateFilter,
		dateFilterDropdownClosed,
		optional,
	};
};
